// @flow

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Dialog,
  Classes,
  Button,
  InputGroup,
  Tooltip,
  Position,
  Tag as BPTag,
  Icon,
  Intent,
} from '@blueprintjs/core';
import TagPill from './tag-pill';
import DeleteTagAlert from './delete-tag-alert';
import { findTagTypeMeta } from './tag-types';
import type { TagTypeMeta } from './tag-types';
import type { TagPermissionsT } from './use-tag-permissions';

type TagT = {
  id: string,
  tagType: string,
  tagValue: string,
};

type Props = {
  isOpen: boolean,
  onClose: () => mixed,
  permissions: TagPermissionsT,
  tagTypes: TagTypeMeta[],
  allTags: $ReadOnlyArray<Object>,
  locations: $ReadOnlyArray<Object>,
  onCreate: (tagType: string, tagValue: string) => Promise<*>,
  onUpdate: (tagId: string, tagValue: string) => Promise<*>,
  onDelete: (tagId: string) => Promise<*>,
  onOpenRefresh: () => mixed,
};

const HeaderBlock = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid #ebf1f5;
`;

const HeaderTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: #182026;
`;

const HeaderSubtitle = styled.div`
  font-size: 12px;
  color: #5c7080;
  margin-bottom: 6px;
`;

const Body = styled.div`
  display: flex;
  height: 420px;
  min-width: 720px;
`;

const LeftPane = styled.div`
  width: 220px;
  border-right: 1px solid #ebf1f5;
  display: flex;
  flex-direction: column;
  padding: 12px 0;
`;

const Stats = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #738694;
  padding: 0 18px 10px;
`;

const TypeRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  border: none;
  outline: none;
  box-shadow: none;
  background: ${(p) => (p.active ? '#e7f1fa' : 'transparent')};
  color: ${(p) => (p.active ? '#106ba3' : '#293742')};
  cursor: pointer;
  border-left: 3px solid ${(p) => (p.active ? p.color : 'transparent')};
  text-align: left;
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 600 : 400)};
  transition: background 80ms ease-in-out, color 80ms ease-in-out;
  &:hover {
    background: ${(p) => (p.active ? '#e7f1fa' : '#f5f8fa')};
  }
  &:focus,
  &:focus-visible,
  &:active,
  &::-moz-focus-inner {
    outline: none;
    border: none;
    box-shadow: none;
    border-left: 3px solid ${(p) => (p.active ? p.color : 'transparent')};
  }
`;

const RightPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f8fa;
`;

const AddRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 18px;
`;

const ValuesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 18px 18px;
`;

const ValuesCard = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #d8e1e8;
  border-radius: 4px;
  overflow: hidden;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #ebf1f5;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #fafbfc;
  }
`;

const Spacer = styled.span`
  flex: 1;
`;

const Usage = styled.span`
  font-size: 11px;
  color: #5c7080;
  white-space: nowrap;
`;

const Empty = styled.div`
  padding: 28px;
  text-align: center;
  color: #5c7080;
  font-style: italic;
  background: white;
  border: 1px solid #d8e1e8;
  border-radius: 4px;
`;

const ManageTagsModal = ({
  isOpen,
  onClose,
  permissions,
  tagTypes,
  allTags,
  locations,
  onCreate,
  onUpdate,
  onDelete,
  onOpenRefresh,
}: Props) => {
  const [activeType, setActiveType] = useState<string>(
    tagTypes[0] ? tagTypes[0].key : '',
  );
  const [newValue, setNewValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<?string>(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingId, setSavingId] = useState<?string>(null);
  const [tagToDelete, setTagToDelete] = useState<?TagT>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      onOpenRefresh();
    } else {
      setNewValue('');
      setEditingId(null);
      setEditingValue('');
      setTagToDelete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const countsByType = useMemo(() => {
    const out: { [string]: number } = {};
    allTags.forEach((t) => {
      out[t.tagType] = (out[t.tagType] || 0) + 1;
    });
    return out;
  }, [allTags]);

  const valuesForActiveType = useMemo(
    () =>
      allTags
        .filter((t) => t.tagType === activeType)
        .sort((a, b) => a.tagValue.localeCompare(b.tagValue)),
    [allTags, activeType],
  );

  const usageCountFor = (tagId: string) =>
    locations.filter((l) => (l.tags || []).includes(tagId)).length;

  const handleCreate = async () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const result = await onCreate(activeType, trimmed);
      if (result) setNewValue('');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    setSavingId(editingId);
    try {
      const result = await onUpdate(editingId, trimmed);
      if (result) {
        setEditingId(null);
        setEditingValue('');
      }
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tagToDelete) return;
    setDeleting(true);
    try {
      await onDelete(tagToDelete.id);
    } finally {
      setDeleting(false);
      setTagToDelete(null);
    }
  };

  const activeTypeMeta = findTagTypeMeta(tagTypes, activeType);

  const renderValueRow = (t: TagT) => {
    const isEditing = editingId === t.id;
    const usage = usageCountFor(t.id);
    const usageLabel = usage === 0 ? 'Unused' : `${usage} location${usage === 1 ? '' : 's'}`;

    return (
      <ValueRow key={t.id}>
        {isEditing ? (
          <InputGroup
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') {
                setEditingId(null);
                setEditingValue('');
              }
            }}
            autoFocus
          />
        ) : (
          <TagPill tag={t} tagTypes={tagTypes} />
        )}
        <Spacer />
        <Usage>{usageLabel}</Usage>
        {isEditing ? (
          <>
            <Button
              icon="tick"
              minimal
              loading={savingId === t.id}
              onClick={handleSaveEdit}
              title="Save"
            />
            <Button
              icon="cross"
              minimal
              onClick={() => {
                setEditingId(null);
                setEditingValue('');
              }}
              title="Cancel"
            />
          </>
        ) : (
          <>
            <Tooltip
              content={
                permissions.canUpdateTag
                  ? 'Rename'
                  : 'You do not have permission to rename tags.'
              }
              position={Position.TOP}
            >
              <Button
                icon="edit"
                minimal
                disabled={!permissions.canUpdateTag}
                onClick={() => {
                  setEditingId(t.id);
                  setEditingValue(t.tagValue);
                }}
              />
            </Tooltip>
            <Tooltip
              content={
                permissions.canDeleteTag
                  ? 'Delete'
                  : 'You do not have permission to delete tags.'
              }
              position={Position.TOP}
            >
              <Button
                icon="trash"
                minimal
                intent={Intent.DANGER}
                disabled={!permissions.canDeleteTag}
                onClick={() => setTagToDelete(t)}
              />
            </Tooltip>
          </>
        )}
      </ValueRow>
    );
  };

  const totalTags = allTags.length;
  const totalTypes = tagTypes.length;

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        icon="tag"
        title="Manage Tags"
        style={{ width: 780, paddingBottom: 0 }}
      >
        <HeaderBlock>
          <HeaderSubtitle>
            Create and edit the tag types and values used across this organisation.
          </HeaderSubtitle>
        </HeaderBlock>
        <Body>
          <LeftPane>
            <Stats>
              {totalTags} tag{totalTags === 1 ? '' : 's'} · {totalTypes} type
              {totalTypes === 1 ? '' : 's'}
            </Stats>
            {tagTypes.map((tt) => (
              <TypeRow
                key={tt.key}
                type="button"
                active={activeType === tt.key}
                color={tt.color}
                onClick={() => {
                  setActiveType(tt.key);
                  setEditingId(null);
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon icon="tag" color={tt.color} />
                  {tt.label}
                </span>
                <BPTag minimal>{countsByType[tt.key] || 0}</BPTag>
              </TypeRow>
            ))}
          </LeftPane>
          <RightPane>
            <AddRow>
              <InputGroup
                fill
                placeholder={`New ${activeTypeMeta.label.toLowerCase()} value…`}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && permissions.canCreateTag) {
                    handleCreate();
                  }
                }}
                disabled={!permissions.canCreateTag}
              />
              <Tooltip
                content={
                  permissions.canCreateTag
                    ? 'Add value'
                    : 'You do not have permission to create tags.'
                }
                position={Position.TOP}
              >
                <Button
                  icon="plus"
                  intent={Intent.PRIMARY}
                  disabled={!permissions.canCreateTag || !newValue.trim()}
                  loading={creating}
                  onClick={handleCreate}
                  text="Add"
                />
              </Tooltip>
            </AddRow>
            <ValuesScroll>
              {valuesForActiveType.length === 0 ? (
                <Empty>No values yet. Add one above to get started.</Empty>
              ) : (
                <ValuesCard>
                  {valuesForActiveType.map(renderValueRow)}
                </ValuesCard>
              )}
            </ValuesScroll>
          </RightPane>
        </Body>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={onClose} text="Done" />
          </div>
        </div>
      </Dialog>
      <DeleteTagAlert
        isOpen={!!tagToDelete}
        tag={tagToDelete}
        usageCount={tagToDelete ? usageCountFor(tagToDelete.id) : 0}
        tagTypes={tagTypes}
        loading={deleting}
        onCancel={() => setTagToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ManageTagsModal;
